import { Component, OnInit } from '@angular/core';
import { FormGroup, FormBuilder } from '@angular/forms';
import { DatePipe } from '@angular/common';
import { ApiService } from '../core/api.service';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-web-punching',
  templateUrl: './web-punching.component.html',
  styleUrls: ['./web-punching.component.css']
})
export class WebPunchingComponent implements OnInit {

  Toast = Swal.mixin({
    toast: true,
    position: 'top-end',
    showConfirmButton: false,
    timer: 3000,
    timerProgressBar: true,
  });
  
  loaderShown: boolean = false;
  
  // Employee Info
  employeeName: string = '';
  employeeId: string = '';
  
  // Form Fields
  selectedDate: Date = new Date();
  maxDate: Date = new Date();
  checkinTime: string = '';
  checkoutTime: string = '';
  checkinLocation: string = '';
  checkoutLocation: string = '';
  
  // Submission Status
  checkinSubmitted: boolean = false;
  checkoutSubmitted: boolean = false;
  
  // Store the MongoDB _id from create response
  currentAttendanceId: string = '';

  // Attendance History
  attendanceHistory: any[] = [];
  filteredAttendanceList: any[] = [];
  summary = {
    totalDays: 0,
    present: 0,
    absent: 0,
    late: 0,
    halfDay: 0,
    holiday: 0
  };

  // Date Range Picker
  range: FormGroup;
  selectedStatus: string = '';
  currentFilter: string = 'all';

  constructor(
    private fb: FormBuilder,
    private datePipe: DatePipe,
    private apiService: ApiService
  ) { 
    this.range = this.fb.group({
      start: [null],
      end: [null]
    });
  }

  ngOnInit(): void {
    this.loadEmployeeInfo();
    this.initializeDates();
    this.checkAttendanceForSelectedDate();
    this.loadAttendanceHistory();
  }

  // ==================== UTILITY METHODS ====================

  private formatDateForAPI(date: Date | null): string {
  if (!date) {
    console.warn('⚠️ formatDateForAPI received null/undefined date');
    return '';
  }
  return this.datePipe.transform(date, 'yyyy-MM-dd') || '';
}

  // ✅ FIXED: This now properly converts local time to UTC
  private formatDateTimeForAPI(date: Date, time: string): string {
    const [hours, minutes] = time.split(':').map(Number);
    
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hoursStr = String(hours).padStart(2, '0');
    const minutesStr = String(minutes).padStart(2, '0');
    
    // Format: "2024-02-04T09:00:00" - NO timezone conversion
    const dateTimeStr = `${year}-${month}-${day}T${hoursStr}:${minutesStr}:00`;
    
    console.log(`🕐 Sending time as-is: ${time} on ${this.formatDateForAPI(date)}`);
    console.log(`   Local DateTime String: ${dateTimeStr}`);
    
    return dateTimeStr;
  }

  loadEmployeeInfo(): void {
    this.employeeName = localStorage.getItem('EmployeeName') || 
                        localStorage.getItem('employee_name') || 
                        '';
    
    this.employeeId = localStorage.getItem('admin_id') || 
                      localStorage.getItem('employee_id') || 
                      '';
    
    console.log('Employee loaded:', { name: this.employeeName, id: this.employeeId });

    if (!this.employeeName || !this.employeeId) {
      this.Toast.fire({
        icon: 'warning',
        title: 'Employee information not found. Please login again.'
      });
    }
  }

  private getAdminId(): string {
    return localStorage.getItem('admin_id') || '';
  }

  initializeDates(): void {
    const today = new Date();
    const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
    
    this.range.setValue({
      start: firstDay,
      end: today
    });
  }

  onDateChange(): void {
    console.log('Date changed to:', this.formatDateForAPI(this.selectedDate));
    this.checkAttendanceForSelectedDate();
  }

  resetToToday(): void {
    this.selectedDate = new Date();
    this.checkAttendanceForSelectedDate();
    this.showInfo('Reset to today\'s date');
  }

  // ==================== CHECK ATTENDANCE FOR SELECTED DATE ====================

  checkAttendanceForSelectedDate(): void {
    if (!this.employeeId) {
      console.error('No employee ID found');
      return;
    }

    this.loaderShown = true;
    const dateStr = this.formatDateForAPI(this.selectedDate);

    console.log('Checking attendance for date:', dateStr);

    const apiUrl = `viewEmployeeAttendance?employee_id=${this.employeeId}&date_filter=${dateStr}`;

    console.log('API URL:', apiUrl);

    this.apiService.get(apiUrl).subscribe({
      next: (response: any) => {
        this.loaderShown = false;
        console.log('Attendance check response:', response);
        
        // Check if we have data
        if (response.success === 'true' && response.data && response.data.length > 0) {
          const record = response.data[0];
          console.log('Found existing record:', record);
          this.displayExistingAttendance(record);
        } else {
          // No data found - allow new entry
          console.log('No attendance found for this date - allowing new entry');
          this.resetFormForNewEntry();
        }
      },
      error: (err: any) => {
        this.loaderShown = false;
        console.error('Error checking attendance:', err);
        // On error, also allow new entry
        this.resetFormForNewEntry();
      }
    });
  }

  private displayExistingAttendance(record: any): void {
    console.log('Displaying existing attendance:', record);
    
    // Extract times from the record (will automatically convert UTC to local time)
    const checkinTime = this.extractTimeFromISO(record.forenoon_checkin_time);
    const checkoutTime = this.extractTimeFromISO(record.afternoon_checkout_time);
    
    console.log('Extracted times - Checkin:', checkinTime, 'Checkout:', checkoutTime);
    
    // Check if we have a valid check-in
    const hasValidCheckin = checkinTime && checkinTime !== '-' && checkinTime !== '00:00';
    
    if (hasValidCheckin) {
      // We have check-in data
      this.checkinSubmitted = true;
      this.checkinTime = checkinTime;
      this.checkinLocation = record.checkin_location || '';
      
      // Store the _id for checkout
      if (record._id) {
        this.currentAttendanceId = record._id;
        console.log('✅ Stored attendance _id:', this.currentAttendanceId);
      }
      
      // Check for checkout
      const hasValidCheckout = checkoutTime && checkoutTime !== '-' && checkoutTime !== '00:00';
      
      if (hasValidCheckout) {
        // Both check-in and check-out exist
        this.checkoutSubmitted = true;
        this.checkoutTime = checkoutTime;
        this.checkoutLocation = record.checkout_location || '';
        console.log('✅ Full attendance record found');
      } else {
        // Only check-in exists, allow checkout
        this.checkoutSubmitted = false;
        this.checkoutTime = '';
        this.checkoutLocation = '';
        console.log('⏳ Check-in found, waiting for check-out');
      }
    } else {
      // No valid check-in found - treat as empty
      console.log('No valid check-in found - allowing new entry');
      this.resetFormForNewEntry();
    }
  }

  private resetFormForNewEntry(): void {
    // Reset all form fields
    this.checkinSubmitted = false;
    this.checkoutSubmitted = false;
    this.checkinTime = '';
    this.checkoutTime = '';
    this.checkinLocation = '';
    this.checkoutLocation = '';
    this.currentAttendanceId = '';
    
    console.log('✅ Form reset - ready for new entry for', this.formatDateForAPI(this.selectedDate));
  }

  // ==================== CHECK-IN ====================

  submitCheckin(): void {
  if (!this.employeeId || !this.employeeName) {
    this.showError('Employee information not found');
    return;
  }

  if (!this.checkinTime) {
    this.showError('Please enter check-in time');
    return;
  }

  // if (!this.checkinLocation) {
  //   this.showError('Please enter check-in location');
  //   return;
  // }

  // ✅ REMOVED: Future time validation

  this.loaderShown = true;

  const requestData = {
    employee_id: this.employeeId,
    employee_name: this.employeeName,
    date: this.formatDateForAPI(this.selectedDate),
    forenoon_checkin_time: this.formatDateTimeForAPI(this.selectedDate, this.checkinTime),
    checkin_location: this.checkinLocation,
    is_web_punching: true
  };

  console.log('Submitting check-in:', requestData);

  this.apiService.post('createEmployeeAttendance', requestData).subscribe({
    next: (response: any) => {
      this.loaderShown = false;
      console.log('Create API response:', response);
      
      const isSuccess = response.success === true || response.success === 'true';
      
      if (isSuccess) {
        this.currentAttendanceId = this.extractIdFromCreateResponse(response);
        
        console.log('✅ Check-in successful, ID:', this.currentAttendanceId);
        
        if (this.currentAttendanceId) {
          this.checkinSubmitted = true;
          this.showSuccess(`Check-in submitted for ${this.formatDateForAPI(this.selectedDate)}!`);
          this.checkAttendanceForSelectedDate();
          this.loadAttendanceHistory();
        } else {
          console.warn('⚠️ No _id in response:', response);
          this.checkinSubmitted = true;
          this.showWarning('Check-in saved but could not get attendance ID');
        }
      } else {
        const errorMsg = response.message || '';
        if (errorMsg.toLowerCase().includes('already marked')) {
          this.checkAttendanceForSelectedDate();
          this.showInfo('Attendance already exists for this date');
        } else {
          this.showError(errorMsg || 'Failed to submit check-in');
        }
      }
    },
    error: (err: any) => {
      this.loaderShown = false;
      console.error('Check-in error:', err);
      const errorMsg = err.error?.message || 'Failed to submit check-in';
      
      if (errorMsg.toLowerCase().includes('already marked')) {
        this.checkAttendanceForSelectedDate();
        this.showInfo('Attendance already exists for this date');
      } else {
        this.showError(errorMsg);
      }
    }
  });
}


  private extractIdFromCreateResponse(response: any): string {
    // Try different possible locations for the _id
    if (response.data && response.data._id) {
      return response.data._id;
    }
    if (response._id) {
      return response._id;
    }
    if (response.id) {
      return response.id;
    }
    if (response.data && response.data.id) {
      return response.data.id;
    }
    return '';
  }

  // ==================== CHECK-OUT ====================

  submitCheckout(): void {
  if (!this.checkoutTime) {
    this.showError('Please enter check-out time');
    return;
  }

  // if (!this.checkoutLocation) {
  //   this.showError('Please enter check-out location');
  //   return;
  // }

  if (!this.currentAttendanceId) {
    this.showError('Cannot submit checkout: Attendance ID missing. Please refresh and try again.');
    console.error('Missing attendance ID for checkout');
    return;
  }

  // Validate checkout is after checkin
  if (this.checkinTime && this.checkoutTime) {
    const checkinMinutes = this.timeToMinutes(this.checkinTime);
    const checkoutMinutes = this.timeToMinutes(this.checkoutTime);
    
    if (checkoutMinutes <= checkinMinutes) {
      this.showError('Check-out time must be after check-in time');
      return;
    }
  }

  // ✅ REMOVED: Future time validation

  this.loaderShown = true;

  const updateData = {
    _id: this.currentAttendanceId,
    afternoon_checkout_time: this.formatDateTimeForAPI(this.selectedDate, this.checkoutTime),
    checkout_location: this.checkoutLocation,
    is_web_punching: true
  };

  console.log('Submitting checkout:', updateData);

  this.apiService.put('updateEmployeeAttendance', updateData).subscribe({
    next: (response: any) => {
      this.loaderShown = false;
      console.log('Update API response:', response);
      
      const isSuccess = response.success === true || response.success === 'true';
      
      if (isSuccess) {
        this.checkoutSubmitted = true;
        this.showSuccess(`Check-out submitted for ${this.formatDateForAPI(this.selectedDate)}!`);
        this.checkAttendanceForSelectedDate();
        this.loadAttendanceHistory();
      } else {
        this.showError(response.message || 'Failed to submit check-out');
      }
    },
    error: (err: any) => {
      this.loaderShown = false;
      console.error('Checkout error:', err);
      this.showError(err.error?.message || 'Failed to submit check-out');
    }
  });
}



  private timeToMinutes(time: string): number {
    const [hours, minutes] = time.split(':').map(Number);
    return hours * 60 + minutes;
  }

  // ==================== ATTENDANCE HISTORY ====================

  loadAttendanceHistory(): void {
  if (!this.employeeId) {
    console.error('❌ No employee ID found');
    return;
  }

  console.log('📅 Date Range Values:', {
    start: this.range.value.start,
    end: this.range.value.end
  });

  this.loaderShown = true;
  
  const fromDate = this.formatDateForAPI(this.range.value.start);
  const toDate = this.formatDateForAPI(this.range.value.end);

  console.log('📅 Formatted Dates:', { fromDate, toDate });

  if (!fromDate || !toDate) {
    console.error('❌ Invalid date range!');
    this.loaderShown = false;
    this.showError('Please select a valid date range');
    return;
  }

  const adminId = this.getAdminId();
  const apiUrl = `attendance-view/employee?admin_id=${adminId}&from_date=${fromDate}&to_date=${toDate}&employee_id=${this.employeeId}`;

  console.log('🔗 Loading attendance history:', apiUrl);

  this.apiService.get(apiUrl).subscribe({
    next: (response: any) => {
      this.loaderShown = false;
      console.log('✅ History API response:', response);
      console.log('✅ Response structure:', JSON.stringify(response, null, 2));
      
      // Handle both boolean true and string 'true'
      const isSuccess = response.success === true || response.success === 'true';
      
      if (isSuccess && response.data) {
        this.attendanceHistory = Array.isArray(response.data) ? response.data : [];
        this.filteredAttendanceList = [...this.attendanceHistory];
        this.calculateSummary();
        console.log('✅ Loaded', this.attendanceHistory.length, 'attendance records');
        
        if (this.attendanceHistory.length === 0) {
          this.showInfo('No attendance records found for the selected date range');
        }
      } else {
        console.error('❌ API returned success: false or no data', {
          success: response.success,
          message: response.message,
          fullResponse: response
        });
        this.attendanceHistory = [];
        this.filteredAttendanceList = [];
        this.calculateSummary();
        this.showError(response.message || 'Failed to load attendance data');
      }
    },
    error: (err: any) => {
      this.loaderShown = false;
      console.error('❌ Error loading history:', err);
      console.error('❌ Full error object:', JSON.stringify(err, null, 2));
      this.attendanceHistory = [];
      this.filteredAttendanceList = [];
      this.calculateSummary();
      this.showError(err.error?.message || 'Failed to load attendance history');
    }
  });
}

  refreshHistory(): void {
    this.loadAttendanceHistory();
    this.showInfo('Attendance history refreshed');
  }

  filterHistory(): void {
  console.log('🔍 Filter button clicked');
  console.log('📅 Current range values:', this.range.value);
  
  // Check if dates are valid
  if (!this.range.value.start || !this.range.value.end) {
    this.showError('Please select both start and end dates');
    return;
  }
  
  // Check if end date is after start date
  if (this.range.value.end < this.range.value.start) {
    this.showError('End date must be after start date');
    return;
  }
  
  this.loadAttendanceHistory();
}

  private calculateSummary(): void {
  this.summary = {
    totalDays: this.attendanceHistory.length,
    present: 0,
    absent: 0,
    late: 0,
    halfDay: 0,
    holiday: 0
  };

  if (this.attendanceHistory.length === 0) {
    return;
  }

  console.log('📊 First attendance record structure:', this.attendanceHistory[0]);

  this.attendanceHistory.forEach(record => {
    // Use the SAME field names as your HTML template (work_in and work_out)
    const checkinTime = this.extractTimeFromISO(record.work_in);
    const checkoutTime = this.extractTimeFromISO(record.work_out);

    const hasCheckin = checkinTime && checkinTime !== '-' && checkinTime !== '00:00';
    const hasCheckout = checkoutTime && checkoutTime !== '-' && checkoutTime !== '00:00';

    // Get the actual display status (this matches what the user sees in the table)
    const displayStatus = this.getDisplayStatus(record).toLowerCase();

    // Count based on what's actually displayed to the user
    if (displayStatus.includes('present') || displayStatus.includes('full day')) {
      this.summary.present++;
    } else if (displayStatus.includes('half day')) {
      this.summary.halfDay++;
    } else if (displayStatus.includes('holiday')) {
      this.summary.holiday++;
    } else if (displayStatus.includes('absent')) {
      this.summary.absent++;
    } else if (displayStatus === 'working' || displayStatus === 'in break') {
      // If employee is still working (not checked out), count as half day
      this.summary.halfDay++;
    } else {
      // Default fallback - if no status matched, check times
      if (hasCheckin && hasCheckout) {
        this.summary.present++;
      } else if (hasCheckin && !hasCheckout) {
        this.summary.halfDay++;
      } else {
        this.summary.absent++;
      }
    }

    // Count late check-ins (check multiple possible field names)
    if (record.is_late_checkin === true || 
        record.is_late === true || 
        record.late_checkin === true) {
      this.summary.late++;
    }
  });

  console.log('📊 Summary calculated:', this.summary);
}

  // ==================== HELPER METHODS FOR TEMPLATE ====================

  // ✅ This converts UTC time from backend to local browser time
  private extractTimeFromISO(isoString: string): string {
    if (!isoString || isoString === '-' || isoString === '00:00' || isoString === '') {
      return '-';
    }
    
    if (typeof isoString === 'string') {
      // Handle ISO format (2024-01-15T09:30:00 or 2024-01-15 09:30:00)
      if (isoString.includes('T') || isoString.includes(' ')) {
        try {
          // Extract just the time part without timezone conversion
          const timePart = isoString.includes('T') 
            ? isoString.split('T')[1] 
            : isoString.split(' ')[1];
          
          if (timePart) {
            // Get HH:mm from the time string
            return timePart.substring(0, 5);
          }
        } catch {
          return '-';
        }
      }
      // Handle time format (09:30 or 09:30:00)
      if (isoString.match(/^\d{2}:\d{2}/)) {
        return isoString.substring(0, 5);
      }
    }
    return '-';
  }

  extractTimeValue(timeValue: any): string {
    return this.extractTimeFromISO(timeValue);
  }

  getDisplayStatus(item: any): string {
  if (!item) return 'Absent';
  
  // Check if employee_status exists and is not null/undefined
  if (item.employee_status !== null && item.employee_status !== undefined) {
    return item.employee_status; // Show "working" or "in break"
  }
  
  // Fallback: Check for attendance_status or status
  const status = item.attendance_status || item.status || item.work_status || '';
  
  if (status) {
    return status;
  }
  
  // Fallback: Determine status based on check-in and check-out times
  // Use work_in and work_out (matching the HTML template)
  const checkinTime = this.extractTimeFromISO(
    item.work_in || item.forenoon_checkin_time || item.checkin_time
  );
  const checkoutTime = this.extractTimeFromISO(
    item.work_out || item.afternoon_checkout_time || item.checkout_time
  );
  
  const hasCheckin = checkinTime !== '-';
  const hasCheckout = checkoutTime !== '-';
  
  if (hasCheckin && hasCheckout) return 'Present';
  if (hasCheckin && !hasCheckout) return 'Half Day';
  
  return 'Absent';
}

/**
 * Get badge class based on status
 */
getStatusBadgeClass(item: any): string {
  const status = this.getDisplayStatus(item) || '';
  const statusLower = status.trim().toLowerCase();
  
  // Check for employee_status values first
  if (statusLower === 'working') {
    return 'bg-success';
  } else if (statusLower === 'in break') {
    return 'bg-warning text-dark';
  } else {
    // For attendance_status fallback values
    switch (statusLower) {
      case 'present':
      case 'full day':
        return 'bg-success';
      case 'half day':
      case 'half day - late in':
      case 'half day - early out':
        return 'bg-warning';
      case 'holiday':
        return 'bg-info text-dark';
      case 'absent':
        return 'bg-danger';
      default:
        return 'bg-secondary';
    }
  }
}

  // getStatusBadgeClass(item: any): string {
  //   const status = (this.getDisplayStatus(item) || '').toLowerCase();
    
  //   if (status.includes('present')) return 'bg-success';
  //   if (status.includes('half day')) return 'bg-warning';
  //   if (status.includes('holiday')) return 'bg-info text-dark';
  //   if (status.includes('absent')) return 'bg-danger';
  //   return 'bg-secondary';
  // }

  filterRecords(filterType: string): void {
    this.currentFilter = filterType;
    
    if (filterType === 'all') {
      this.filteredAttendanceList = [...this.attendanceHistory];
      return;
    }

    this.filteredAttendanceList = this.attendanceHistory.filter(item => {
      const status = this.getDisplayStatus(item).toLowerCase();
      
      switch (filterType) {
        case 'absent': return status.includes('absent');
        case 'present': return status.includes('present');
        case 'half day': return status.includes('half day');
        case 'holiday': return status.includes('holiday');
        default: return true;
      }
    });
  }
  filterByStatus(): void {
  console.log('🔍 Filtering by status:', this.selectedStatus);
  
  if (!this.selectedStatus || this.selectedStatus === '') {
    this.filteredAttendanceList = [...this.attendanceHistory];
    return;
  }

  this.filteredAttendanceList = this.attendanceHistory.filter(item => {
    const status = this.getDisplayStatus(item).toLowerCase();
    const filterValue = this.selectedStatus.toLowerCase();
    
    // Handle special cases
    if (filterValue === 'late') {
      return item.is_late_checkin === true || item.is_late === true;
    }
    
    return status.includes(filterValue);
  });
  
  console.log(`✅ Filtered ${this.filteredAttendanceList.length} records`);
}

  isFilterActive(filterType: string): boolean {
    return this.currentFilter === filterType;
  }

  resetFilters(): void {
    this.initializeDates();
    this.selectedStatus = '';
    this.currentFilter = 'all';
    this.loadAttendanceHistory();
  }

  clearDateRange(): void {
    this.initializeDates();
    this.loadAttendanceHistory();
  }

  // ==================== TOAST HELPERS ====================

  private showSuccess(message: string): void {
    this.Toast.fire({ 
      icon: 'success', 
      title: message
    });
  }

  private showError(message: string): void {
    this.Toast.fire({ 
      icon: 'error', 
      title: message
    });
  }

  private showInfo(message: string): void {
    this.Toast.fire({ 
      icon: 'info', 
      title: message
    });
  }

  private showWarning(message: string): void {
    this.Toast.fire({ 
      icon: 'warning', 
      title: message
    });
  }
}